import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { NewsItem } from "@/types/news";
import { getCategoryByKey, relativeTime, formatViews } from "@/utils/news";

interface HeroCarouselProps {
  items: NewsItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const navigate = useNavigate();
  const [active, setActive] = useState(0);
  const touchStart = useRef<number | null>(null);
  const count = items.length;

  useEffect(() => {
    if (count <= 1) return;
    const timer = setInterval(() => {
      setActive((v) => (v + 1) % count);
    }, 5000);
    return () => clearInterval(timer);
  }, [count]);

  if (count === 0) return null;

  const go = (index: number) => setActive((index + count) % count);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStart.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStart.current === null) return;
    const diff = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(diff) > 50) {
      go(diff < 0 ? active + 1 : active - 1);
    }
    touchStart.current = null;
  };

  return (
    <section className="relative w-full h-[440px] md:h-[540px] overflow-hidden rounded-lg">
      <div
        className="w-full h-full touch-pan-y"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {items.map((item, index) => {
          const category = getCategoryByKey(item.category);
          return (
            <div
              key={item.id}
              className={`absolute inset-0 transition-opacity duration-700 ${
                index === active ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"
              }`}
            >
              <img
                src={item.cover}
                alt={item.title}
                title={`${item.title} - 51吃瓜官网`}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/45 to-black/25"></div>
              <div className="absolute inset-x-0 bottom-0 p-5 md:p-10">
                <div className="max-w-3xl">
                  <div className="flex items-center gap-3 mb-3">
                    {category && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-primary-500 text-background-50">
                        <i className={category.icon}></i>
                        {category.name}
                      </span>
                    )}
                    <span className="text-xs text-white/80 flex items-center gap-1">
                      <i className="ri-fire-line"></i>
                      今日爆点
                    </span>
                  </div>
                  <h3
                    onClick={() => navigate(`/detail/${item.id}`)}
                    className="text-xl md:text-3xl font-black text-white leading-snug cursor-pointer hover:text-primary-200 transition-colors line-clamp-2"
                  >
                    {item.title}
                  </h3>
                  <p className="hidden md:block text-sm text-white/80 mt-3 line-clamp-2 max-w-2xl">
                    {item.excerpt}
                  </p>
                  <div className="flex items-center gap-4 mt-4 text-xs text-white/75">
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {relativeTime(item.publishedAt)}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {formatViews(item.views)} 阅读
                    </span>
                    <button
                      type="button"
                      onClick={() => navigate(`/detail/${item.id}`)}
                      className="ml-1 inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      阅读全文
                      <i className="ri-arrow-right-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* 左右箭头 */}
      {count > 1 && (
        <>
          <button
            type="button"
            aria-label="上一条"
            onClick={() => go(active - 1)}
            className="hidden sm:flex absolute left-4 md:left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-black/35 text-white hover:bg-black/55 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>
          <button
            type="button"
            aria-label="下一条"
            onClick={() => go(active + 1)}
            className="hidden sm:flex absolute right-4 md:right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-black/35 text-white hover:bg-black/55 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-2xl"></i>
          </button>
        </>
      )}

      {/* 指示点 */}
      {count > 1 && (
        <div className="absolute bottom-4 right-5 md:right-10 z-20 flex items-center gap-2">
          {items.map((item, index) => (
            <button
              key={item.id}
              type="button"
              aria-label={`切换到第 ${index + 1} 条`}
              onClick={() => go(index)}
              className={`h-2 rounded-full transition-all cursor-pointer ${
                index === active ? "w-6 bg-primary-500" : "w-2 bg-white/60 hover:bg-white"
              }`}
            ></button>
          ))}
        </div>
      )}
    </section>
  );
}